import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../ui/LoadingSpinner'
import ConfirmModal from '../ui/ConfirmModal'

interface RedditAppCredentialsFormProps {
  existingApp?: {
    app_name: string
    client_id_masked?: string
    client_secret_masked?: string
    user_agent: string
  }
  onSave: (data: RedditAppFormData) => Promise<void>
  onDelete?: () => Promise<void>
  isUpdating: boolean
}

export interface RedditAppFormData {
  app_name: string
  client_id: string
  client_secret: string
  user_agent: string
}

const schema = yup.object({
  app_name: yup.string().required('App name is required'),
  client_id: yup.string().required('Client ID is required'),
  client_secret: yup.string().when('$isUpdate', {
    is: true,
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.required('Client Secret is required'),
  }),
  user_agent: yup.string().required('User Agent is required'),
})

const RedditAppCredentialsForm: React.FC<RedditAppCredentialsFormProps> = ({
  existingApp,
  onSave,
  onDelete,
  isUpdating,
}) => {
  const [showSecret, setShowSecret] = useState(false)
  const [isEditing, setIsEditing] = useState(!existingApp)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const isUpdate = !!existingApp

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RedditAppFormData>({
    resolver: yupResolver(schema),
    context: { isUpdate },
    defaultValues: {
      app_name: existingApp?.app_name || 'My Reddit App',
      client_id: existingApp?.client_id_masked || '',
      client_secret: '',
      user_agent: existingApp?.user_agent || 'ReddMinerLeads/1.0 by /u/yourusername',
    },
  })

  const handleFormSubmit = async (data: RedditAppFormData) => {
    await onSave(data)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
    reset()
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!onDelete) return

    setShowDeleteConfirm(false)
    setIsDeleting(true)
    try {
      await onDelete()
      // Success toast will be shown by parent component
    } catch (error) {
      // Error toast will be shown by parent component
      throw error
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }

  // If existing app and not editing, show readonly view
  if (existingApp && !isEditing) {
    return (
      <div className="border rounded-lg p-6 bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-gray-900">Current Reddit App</h4>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Update Credentials
            </button>
            {onDelete && (
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-700 font-medium inline-flex items-center disabled:opacity-50"
              >
                {isDeleting ? (
                  <LoadingSpinner size="sm" className="mr-1" />
                ) : (
                  <TrashIcon className="h-4 w-4 mr-1" />
                )}
                Delete
              </button>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">App Name</label>
            <p className="mt-1 text-sm text-gray-900">{existingApp.app_name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Client ID</label>
            <p className="mt-1 text-sm text-gray-900 font-mono">{existingApp.client_id_masked}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Client Secret</label>
            <p className="mt-1 text-sm text-gray-900 font-mono">{existingApp.client_secret_masked}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">User Agent</label>
            <p className="mt-1 text-sm text-gray-900 font-mono">{existingApp.user_agent}</p>
          </div>
        </div>
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Reddit App?"
          message="This will disconnect your Reddit account, stop all monitoring, and remove all app data. This action cannot be undone."
          confirmText="Delete App"
          cancelText="Cancel"
          isDangerous={true}
        />
      </div>
    )
  }

  // Editing mode or new app
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="border rounded-lg p-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            App Name <span className="text-red-500">*</span>
          </label>
          <input
            {...register('app_name')}
            type="text"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="My Reddit App"
          />
          {errors.app_name && (
            <p className="mt-1 text-sm text-red-600">{errors.app_name.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">A friendly name for your Reddit app</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Client ID <span className="text-red-500">*</span>
          </label>
          <input
            {...register('client_id')}
            type="text"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder="abc123XYZ"
          />
          {errors.client_id && (
            <p className="mt-1 text-sm text-red-600">{errors.client_id.message}</p>
          )}
          {existingApp?.client_id_masked && (
            <p className="mt-1 text-xs text-gray-500">Current: {existingApp.client_id_masked}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Client Secret {!isUpdate && <span className="text-red-500">*</span>}
          </label>
          <div className="mt-1 relative">
            {/* Hidden username field for accessibility */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              style={{ display: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <input
              {...register('client_secret')}
              type={showSecret ? 'text' : 'password'}
              autoComplete="current-password"
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10 font-mono text-sm"
              placeholder={isUpdate ? 'Leave blank to keep current secret' : 'Enter client secret'}
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showSecret ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.client_secret && (
            <p className="mt-1 text-sm text-red-600">{errors.client_secret.message}</p>
          )}
          {existingApp?.client_secret_masked && (
            <p className="mt-1 text-xs text-gray-500">Current: {existingApp.client_secret_masked}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            User Agent <span className="text-red-500">*</span>
          </label>
          <input
            {...register('user_agent')}
            type="text"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder="ReddMinerLeads/1.0 by /u/yourusername"
          />
          {errors.user_agent && (
            <p className="mt-1 text-sm text-red-600">{errors.user_agent.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Format: AppName/Version by /u/yourRedditUsername
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-6 mt-6 border-t">
        {isUpdate && (
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isUpdating}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 ml-auto"
        >
          {isUpdating ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            'Save Credentials'
          )}
        </button>
      </div>
    </form>
  )
}

export default RedditAppCredentialsForm
